import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";
import { canAccessTenantAdmin, getRoleNames, hasAnyPermission } from "../../utils/tenantAdminAccess";
import { TENANT_GOVERNANCE_PERMISSION_GROUPS } from "../../constants/tenantGovernancePermissions";
import {
  TENANT_HEALTH_STATUS_TYPES,
  buildTenantHealthReport,
} from "./tenantHealthEngine";

function asUserList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }) {
  const meta = TENANT_HEALTH_STATUS_TYPES[status] || TENANT_HEALTH_STATUS_TYPES.inactive;
  return (
    <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${meta.badgeClass}`}>
      {meta.label}
    </span>
  );
}

function HealthCard({ item, canOpenAction }) {
  const meta = TENANT_HEALTH_STATUS_TYPES[item.status] || TENANT_HEALTH_STATUS_TYPES.inactive;
  const showAction = item.status !== "healthy" && canOpenAction(item);
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">{item.label}</h3>
        <StatusBadge status={item.status} />
      </div>
      <p className="text-lg font-bold text-slate-900">{item.value}</p>
      <div className="mt-2 flex items-start gap-2 text-xs text-slate-600">
        <span className={`mt-1 inline-block h-2 w-2 rounded-full ${meta.dotClass}`} />
        <span>{item.note}</span>
      </div>
      {showAction ? (
        <div className="mt-3">
          <Link
            to={item.actionTo}
            className="inline-flex items-center rounded-md border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-100"
          >
            {item.actionLabel}
          </Link>
        </div>
      ) : null}
    </article>
  );
}

function SectionBlock({ title, items, canOpenAction }) {
  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold text-slate-900">{title}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <HealthCard key={item.key} item={item} canOpenAction={canOpenAction} />
        ))}
      </div>
    </section>
  );
}

export default function TenantAdminStatusPage() {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState([]);
  const [data, setData] = useState({
    settings: null,
    users: [],
    workorders: [],
    photos: { items: [], pagination: { total: 0 } },
    supportTickets: { items: [], pagination: { total: 0 } },
    apiErrors: [],
    mailErrors: [],
    warningLogs: [],
  });

  useEffect(() => {
    let alive = true;

    async function loadData() {
      setLoading(true);
      setLoadErrors([]);

      const requests = await Promise.allSettled([
        api.get("/tenant/settings"),
        api.get("/users", { params: { includeSessionInsights: true } }),
        api.get("/werkbon"),
        api.get("/photos/pl-overview", { params: { limit: 120, page: 1 } }),
        api.get("/support-tickets", { params: { limit: 100, page: 1 } }),
        api.get("/auditlogs", { params: { action: "api_error", limit: 10 } }),
        api.get("/auditlogs", { params: { q: "mail", severity: "error", limit: 10 } }),
        api.get("/auditlogs", { params: { severity: "warning", limit: 10 } }),
      ]);

      if (!alive) return;

      const nextErrors = [];
      const nextData = {
        settings: null,
        users: [],
        workorders: [],
        photos: { items: [], pagination: { total: 0 } },
        supportTickets: { items: [], pagination: { total: 0 } },
        apiErrors: [],
        mailErrors: [],
        warningLogs: [],
      };

      const [
        settingsRes,
        usersRes,
        workordersRes,
        photosRes,
        supportRes,
        apiErrorsRes,
        mailErrorsRes,
        warningRes,
      ] = requests;

      if (settingsRes.status === "fulfilled") {
        nextData.settings = settingsRes.value?.data || null;
      } else {
        nextErrors.push("Bedrijfsinstellingen konden niet worden geladen.");
      }

      if (usersRes.status === "fulfilled") {
        nextData.users = asUserList(usersRes.value?.data);
      } else {
        nextErrors.push("Gebruikersinzichten zijn beperkt (geen toegang of fout).");
      }

      if (workordersRes.status === "fulfilled") {
        nextData.workorders = Array.isArray(workordersRes.value?.data) ? workordersRes.value.data : [];
      } else {
        nextErrors.push("Werkbonsignalen zijn beperkt (geen toegang of fout).");
      }

      if (photosRes.status === "fulfilled") {
        const payload = photosRes.value?.data || {};
        nextData.photos = {
          items: Array.isArray(payload?.items) ? payload.items : [],
          pagination: payload?.pagination || { total: 0 },
        };
      } else {
        nextErrors.push("Foto-activiteit is beperkt (geen toegang of fout).");
      }

      if (supportRes.status === "fulfilled") {
        const payload = supportRes.value?.data || {};
        nextData.supportTickets = {
          items: Array.isArray(payload?.items) ? payload.items : [],
          pagination: payload?.pagination || { total: 0 },
        };
      } else {
        nextErrors.push("Supportsignalen zijn beperkt (geen toegang of fout).");
      }

      if (apiErrorsRes.status === "fulfilled") {
        nextData.apiErrors = Array.isArray(apiErrorsRes.value?.data) ? apiErrorsRes.value.data : [];
      }
      if (mailErrorsRes.status === "fulfilled") {
        nextData.mailErrors = Array.isArray(mailErrorsRes.value?.data) ? mailErrorsRes.value.data : [];
      }
      if (warningRes.status === "fulfilled") {
        nextData.warningLogs = Array.isArray(warningRes.value?.data) ? warningRes.value.data : [];
      }

      setData(nextData);
      setLoadErrors(nextErrors);
      setLoading(false);
    }

    loadData();

    api.post("/tenant/settings/status-viewed").catch(() => {
      // audit logging is best-effort
    });

    return () => {
      alive = false;
    };
  }, []);

  const report = useMemo(
    () =>
      buildTenantHealthReport({
        settings: data.settings,
        users: data.users,
        workorders: data.workorders,
        photos: data.photos,
        supportTickets: data.supportTickets,
        apiErrors: data.apiErrors,
        mailErrors: data.mailErrors,
        warnings: data.warningLogs,
      }),
    [data]
  );

  const topMeta =
    TENANT_HEALTH_STATUS_TYPES[report.topStatus] || TENANT_HEALTH_STATUS_TYPES.inactive;
  const readinessMeta =
    TENANT_HEALTH_STATUS_TYPES[report.tenantReadiness] || TENANT_HEALTH_STATUS_TYPES.inactive;
  const canOpenAction = useMemo(() => {
    return (item) => {
      if (!item?.actionTo || !item?.actionLabel || !user) return false;

      const roleNames = new Set(getRoleNames(user));
      if (roleNames.has("tenantadmin") || roleNames.has("platformadmin")) return true;
      if (!canAccessTenantAdmin(user)) return false;

      const requiredPermissions = Array.isArray(item?.actionPermissionsAny)
        ? item.actionPermissionsAny
        : TENANT_GOVERNANCE_PERMISSION_GROUPS.statusView;
      if (requiredPermissions.length === 0) return true;

      return hasAnyPermission(user, requiredPermissions);
    };
  }, [user]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Monitoring</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">Inrichtingsstatus & Monitoring</h1>
          <p className="mt-2 text-sm text-slate-600">
            Controleer hoe compleet deze omgeving is ingericht en welke signalen aandacht vragen.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className={`rounded-full border px-2 py-0.5 font-medium ${topMeta.badgeClass}`}>
              Algemene status: {topMeta.label}
            </span>
            <span className={`rounded-full border px-2 py-0.5 font-medium ${readinessMeta.badgeClass}`}>
              Inrichtingsgraad: {report.readinessScore}% ({report.onboardingProgress})
            </span>
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-slate-700">
              Laatste activiteit: {formatDateTime(report.lastActivityAt)}
            </span>
          </div>
          <div className="mt-3">
            <Link to="/tenant-admin" className="text-sm font-medium text-slate-700 underline underline-offset-2">
              Terug naar Bedrijfsinrichting
            </Link>
          </div>
        </header>

        {loadErrors.length > 0 ? (
          <section className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
            <h2 className="text-sm font-semibold text-amber-900">Informatie met fallback geladen</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
              {loadErrors.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {loading ? <p className="text-sm text-slate-600">Monitoringsignalen laden...</p> : null}

        {!loading ? (
          <div className="space-y-7">
            <SectionBlock
              title="1. Configuratie status"
              items={report.sections.configuration}
              canOpenAction={canOpenAction}
            />
            <SectionBlock
              title="2. Activiteit"
              items={report.sections.activity}
              canOpenAction={canOpenAction}
            />
            <SectionBlock
              title="3. Integratie gezondheid"
              items={report.sections.integrations}
              canOpenAction={canOpenAction}
            />
            <SectionBlock
              title="4. Proces signalen"
              items={report.sections.process}
              canOpenAction={canOpenAction}
            />
            <SectionBlock
              title="5. Systeemsignalen"
              items={report.sections.system}
              canOpenAction={canOpenAction}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
